import React, { useState, useEffect, useCallback } from 'react';
import { IoClose } from 'react-icons/io5';
import automationService from '../../services/automationService';
import {
  DrawerOverlay,
  DrawerPanel,
  DrawerHeader,
  DrawerTabs,
  DrawerTab,
  DrawerBody,
  ExecutionRow,
  ExecutionRowTop,
  ExecutionLeadName,
  ExecutionStatusBadge,
  ExecutionMeta,
  ExecutionError,
  DrawerFooter,
  DrawerPageButton,
  DrawerEmpty,
} from './FlowBuilder.styles';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'completed', label: 'Completed' },
  { key: 'failed', label: 'Failed' },
  { key: 'running', label: 'Running' },
  { key: 'pending', label: 'Pending' },
];

const PAGE_SIZE = 20;

function formatDuration(ms) {
  if (!ms) return null;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function formatTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const ExecutionsDrawer = ({ open, onClose, workflowId, nodeId, initialStatus = 'all', nodeLabel }) => {
  const [activeTab, setActiveTab] = useState(initialStatus);
  const [executions, setExecutions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchExecutions = useCallback(async (tab, pg) => {
    if (!workflowId) return;
    setLoading(true);
    try {
      const params = { page: pg, limit: PAGE_SIZE };
      if (tab !== 'all') params.status = tab;
      if (nodeId) params.nodeId = nodeId;
      const res = await automationService.getWorkflowExecutions(workflowId, params);
      if (res.success) {
        setExecutions(res.data.executions || []);
        setTotal(res.data.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch executions:', err.message);
    } finally {
      setLoading(false);
    }
  }, [workflowId, nodeId]);

  useEffect(() => {
    if (open) {
      setActiveTab(initialStatus);
      setPage(1);
      fetchExecutions(initialStatus, 1);
    }
  }, [open, initialStatus, fetchExecutions]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    fetchExecutions(tab, 1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchExecutions(activeTab, newPage);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <>
      <DrawerOverlay $open={open} onClick={onClose} />
      <DrawerPanel $open={open}>
        <DrawerHeader>
          <h3>{nodeLabel ? `${nodeLabel} — Executions` : 'Executions'}</h3>
          <button onClick={onClose}><IoClose /></button>
        </DrawerHeader>

        <DrawerTabs>
          {STATUS_TABS.map(tab => (
            <DrawerTab
              key={tab.key}
              $active={activeTab === tab.key}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
            </DrawerTab>
          ))}
        </DrawerTabs>

        <DrawerBody>
          {loading ? (
            <DrawerEmpty>Loading...</DrawerEmpty>
          ) : executions.length === 0 ? (
            <DrawerEmpty>No executions found</DrawerEmpty>
          ) : (
            executions.map(exec => {
              const leadName = exec.userLead
                ? `${exec.userLead.first_name || ''} ${exec.userLead.last_name || ''}`.trim() || exec.userLead.linkedin_url || `Lead #${exec.leadId}`
                : `Lead #${exec.leadId}`;
              const senderName = exec.linkedinProfile
                ? `${exec.linkedinProfile.firstName || ''} ${exec.linkedinProfile.lastName || ''}`.trim()
                : null;

              return (
                <ExecutionRow key={exec.id}>
                  <ExecutionRowTop>
                    <ExecutionLeadName title={leadName}>{leadName}</ExecutionLeadName>
                    <ExecutionStatusBadge $status={exec.status}>{exec.status}</ExecutionStatusBadge>
                  </ExecutionRowTop>

                  <ExecutionMeta>
                    {exec.status === 'pending' || exec.status === 'paused'
                      ? exec.scheduledAt && <span>Scheduled: {formatTime(exec.scheduledAt)}</span>
                      : exec.startedAt && <span>Started: {formatTime(exec.startedAt)}</span>
                    }
                    {exec.status === 'failed'
                      ? exec.completedAt && <span>Failed at: {formatTime(exec.completedAt)}</span>
                      : exec.status !== 'pending' && exec.status !== 'paused' && exec.completedAt && <span>Done: {formatTime(exec.completedAt)}</span>
                    }
                    {exec.duration && <span>{formatDuration(exec.duration)}</span>}
                    {senderName && <span>via {senderName}</span>}
                  </ExecutionMeta>

                  {exec.status === 'failed' && exec.error && <ExecutionError>{exec.error}</ExecutionError>}
                </ExecutionRow>
              );
            })
          )}
        </DrawerBody>

        <DrawerFooter>
          <span>{total} execution{total !== 1 ? 's' : ''}</span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <DrawerPageButton disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
              Prev
            </DrawerPageButton>
            <span>{page} / {totalPages || 1}</span>
            <DrawerPageButton disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>
              Next
            </DrawerPageButton>
          </div>
        </DrawerFooter>
      </DrawerPanel>
    </>
  );
};

export default ExecutionsDrawer;
